import { theme as antdTheme, type ThemeConfig } from 'antd';
import { COLORS, COLORS_DARK, type ColorToken, type ColorTokenDark } from './colors';
import { SCALE, type ScaleTier } from './scale';

/**
 * Inter Typography
 * Professional enterprise font designed for computer screens
 */
const FONT_FAMILY = "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

type ThemePalette = ColorToken | ColorTokenDark;

const createTheme = ({
  palette,
  isDark,
  tier,
}: {
  palette: ThemePalette;
  isDark: boolean;
  tier: ScaleTier;
}): ThemeConfig => ({
  algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
  token: {
    // Brand colors
    colorPrimary: palette.brand.primary,
    colorSuccess: palette.semantic.success,
    colorWarning: palette.semantic.warning,
    colorError: palette.semantic.error,
    colorInfo: palette.semantic.info,

    // Background colors
    colorBgContainer: palette.background.card,
    colorBgElevated: palette.background.card,
    colorBgLayout: palette.background.page,
    colorBgSpotlight: palette.background.hover,
    colorBgTextHover: palette.background.hover,

    // Text colors
    colorText: palette.text.primary,
    colorTextSecondary: palette.text.secondary,
    colorTextTertiary: palette.text.disabled,
    colorTextQuaternary: palette.text.disabled,

    // Border colors
    colorBorder: palette.border.default,
    colorBorderSecondary: palette.border.light,

    // Typography - Fluid Glass
    fontFamily: FONT_FAMILY,
    fontSize: SCALE.fontSizeBody,
    fontSizeHeading1: 38,
    fontSizeHeading2: 30,
    fontSizeHeading3: 24,
    fontSizeHeading4: 20,
    fontSizeHeading5: 16,
    fontWeightStrong: 600,

    // Layout
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 6,
    wireframe: false,

    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,

    // Control sizes: 32px with a pointer, 40px on touch (see scale.ts)
    controlHeight: tier.controlHeight,
    controlHeightSM: tier.controlHeightSM,
    controlHeightLG: tier.controlHeightLG,

    // Shadows (subtle, modern)
    boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1)',
    boxShadowSecondary: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  },

  components: {
    Layout: {
      headerBg: palette.background.card,
      headerHeight: tier.headerHeight,
      siderBg: palette.sidebar.background,
      bodyBg: palette.background.page,
      footerBg: palette.background.page,
    },

    Menu: {
      darkItemBg: palette.sidebar.itemBg,
      darkSubMenuItemBg: palette.sidebar.background,
      darkItemSelectedBg: palette.sidebar.itemSelectedBg,
      darkItemHoverBg: palette.sidebar.itemHoverBg,
      darkItemColor: palette.sidebar.itemText,
      darkItemSelectedColor: palette.sidebar.itemActiveText,
      darkItemHoverColor: palette.sidebar.itemActiveText,
      itemHeight: 44,
      itemMarginInline: 8,
      itemBorderRadius: 8,
      fontWeightStrong: 500,
    },

    Table: {
      headerBg: palette.background.tableHeader,
      headerColor: palette.text.primary,
      headerSortActiveBg: palette.background.hover,
      rowHoverBg: palette.background.hover,
      borderColor: palette.border.default,
      headerSplitColor: palette.border.default,
      // One density for every table, whatever `size` a page passes.
      cellFontSize: tier.tableFontSize,
      cellFontSizeMD: tier.tableFontSize,
      cellFontSizeSM: tier.tableFontSize,
      cellPaddingBlock: SCALE.tableCellPaddingBlock,
      cellPaddingBlockMD: SCALE.tableCellPaddingBlock,
      cellPaddingBlockSM: SCALE.tableCellPaddingBlock,
      cellPaddingInline: SCALE.tableCellPaddingInline,
      cellPaddingInlineMD: SCALE.tableCellPaddingInline,
      cellPaddingInlineSM: SCALE.tableCellPaddingInline,
    },

    Card: {
      headerBg: 'transparent',
      colorBgContainer: palette.background.card,
      colorBorderSecondary: palette.border.default,
      borderRadiusLG: 12,
      bodyPadding: tier.cardPadding,
      headerPadding: tier.cardPadding,
      headerHeight: tier.cardHeaderHeight,
      headerFontSize: SCALE.fontSizeCardTitle,
    },

    Descriptions: {
      // A bordered row is the detail-page twin of a table row.
      padding: tier.detailCellPaddingBlock,
      paddingSM: tier.detailCellPaddingBlock,
      paddingLG: SCALE.space[2],
      itemPaddingBottom: SCALE.space[1],
    },

    Button: {
      primaryColor: palette.text.inverse,
      colorPrimary: palette.brand.primary,
      colorPrimaryHover: palette.brand.primaryHover,
      colorPrimaryActive: palette.brand.primaryHover,
      borderRadius: 8,
      fontWeight: 500,
      paddingInline: 16,
    },

    Input: {
      colorBgContainer: palette.background.input,
      colorBorder: palette.border.default,
      hoverBorderColor: palette.brand.primary,
      activeBorderColor: palette.brand.primary,
      borderRadius: 8,
      inputFontSize: tier.inputFontSize,
      inputFontSizeSM: tier.inputFontSize,
    },

    InputNumber: {
      inputFontSize: tier.inputFontSize,
      inputFontSizeSM: tier.inputFontSize,
    },

    DatePicker: {
      inputFontSize: tier.inputFontSize,
      inputFontSizeSM: tier.inputFontSize,
      // Ant Design derives the panel from the control heights. Pinned, so that the taller
      // touch controls do not make the calendar and the time columns wider.
      cellWidth: 36,
      timeColumnWidth: 56,
    },

    Select: {
      colorBgContainer: palette.background.input,
      colorBorder: palette.border.default,
      borderRadius: 8,
      // The field text follows the inputs. The options of the dropdown stay at body size.
      fontSize: tier.inputFontSize,
      optionFontSize: SCALE.fontSizeBody,
    },

    Modal: {
      headerBg: palette.background.card,
      contentBg: palette.background.card,
      borderRadiusLG: 16,
    },

    Form: {
      labelColor: palette.text.primary,
      labelFontSize: SCALE.fontSizeLabel,
      itemMarginBottom: SCALE.formItemGap,
      verticalLabelPadding: `0 0 ${String(SCALE.space[0])}px`,
    },

    Typography: {
      colorText: palette.text.primary,
      colorTextSecondary: palette.text.secondary,
      fontWeightStrong: 600,
    },

    Dropdown: {
      colorBgElevated: palette.background.card,
      borderRadiusLG: 12,
    },

    Alert: {
      colorInfoBg: palette.semantic.infoBg,
      colorInfoBorder: palette.brand.primary,
      colorSuccessBg: palette.semantic.successBg,
      colorSuccessBorder: palette.semantic.success,
      colorWarningBg: palette.semantic.warningBg,
      colorWarningBorder: palette.semantic.warning,
      colorErrorBg: palette.semantic.errorBg,
      colorErrorBorder: palette.semantic.error,
    },

    Statistic: {
      contentFontSize: 24,
    },

    Breadcrumb: {
      colorText: palette.text.secondary,
      linkColor: palette.text.secondary,
      linkHoverColor: palette.brand.primary,
      separatorColor: palette.text.disabled,
    },

    Pagination: {
      colorPrimary: palette.brand.primary,
      colorPrimaryHover: palette.brand.primaryHover,
      borderRadius: 8,
    },

    Tag: {
      borderRadiusSM: 6,
    },

    Avatar: {
      colorBgBase: palette.brand.primary,
    },

    Spin: {
      colorPrimary: palette.brand.primary,
    },

    Tooltip: {
      colorBgSpotlight: isDark ? '#2f3742' : '#1f2937',
      colorTextLightSolid: '#ffffff',
      borderRadius: 8,
    },
  },
});

const THEMES = {
  light: {
    pointer: createTheme({ palette: COLORS, isDark: false, tier: SCALE.pointer }),
    touch: createTheme({ palette: COLORS, isDark: false, tier: SCALE.touch }),
  },
  dark: {
    pointer: createTheme({ palette: COLORS_DARK, isDark: true, tier: SCALE.pointer }),
    touch: createTheme({ palette: COLORS_DARK, isDark: true, tier: SCALE.touch }),
  },
} as const;

export const getTheme = ({
  isDark,
  isTouchTier,
}: {
  isDark: boolean;
  isTouchTier: boolean;
}): ThemeConfig => THEMES[isDark ? 'dark' : 'light'][isTouchTier ? 'touch' : 'pointer'];
